import { useTransactionTimer } from "@bkry/bowline-components/CustomHook";
import PropTypes from "prop-types";
import React from "react";
import { useSsrEffect } from "@issr/core";

import { FaCheck, FaPause } from "react-icons/fa";
import { useDispatch } from "react-redux";
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";

const GenericProcessingModal = (
  {
    deliveryType,
    children,
    title,
    processing,
    processReset,
    steps,
    secondaryAction,
    primaryAction,
    skipDispatch,
  },
  context
) => {
  const dispatch = useDispatch();
  const [transactionTimeInSeconds, resetTransactionTimer] =
    useTransactionTimer(processing);
  const MAX_SECONDS_UNTIL_CLOSE_MESSAGE = 30;

  const handleModalReset = () => {
    if (processReset) {
      if (skipDispatch) {
        processReset();
      } else {
        dispatch(processReset());
      }
    }
  };

  useSsrEffect(() => {
    if (!processing && transactionTimeInSeconds) {
      resetTransactionTimer();
    }
  }, [transactionTimeInSeconds, processing]);

  return (
    <Modal isOpen={processing ? true : false} toggle={handleModalReset}>
      <ModalHeader toggle={handleModalReset}>
        {title || context.t("Processing")}
      </ModalHeader>
      <ModalBody>
        {children}
        {steps?.map((step) => (
          <Row key={step.title}>
            <Col xs={2} className="text-center">
              {step.status === "pending" ? (
                <FaPause size={"1rem"} className="text-muted" />
              ) : (
                <>
                  {step.status === "processing" ? (
                    <Spinner
                      style={{ width: "1rem", height: "1rem" }}
                      color="primary"
                    />
                  ) : (
                    <FaCheck size={"1rem"} className="text-primary" />
                  )}
                </>
              )}
            </Col>
            <Col
              xs={10}
              className={step.status === "processing" ? "text-muted" : null}
            >
              <p className="fw-bold">{step.title}</p>
              <p>{step.content}</p>
            </Col>
          </Row>
        ))}

        {transactionTimeInSeconds >= MAX_SECONDS_UNTIL_CLOSE_MESSAGE && (
          <p className="small">
            {deliveryType === "bowline_relay"
              ? context.t(
                  "The airdrop is handled in the background and might experience delays beyond the usual timeframe due to network congestion."
                )
              : context.t(
                  "This transaction takes longer than usual. You can close this modal without canceling the transaction."
                )}
          </p>
        )}
      </ModalBody>
      {(primaryAction || secondaryAction) && (
        <ModalFooter>
          {secondaryAction && (
            <Button
              color="primary"
              outline
              onClick={() =>
                secondaryAction
                  ? skipDispatch
                    ? secondaryAction?.action()
                    : dispatch(secondaryAction?.action())
                  : null
              }
            >
              {secondaryAction?.title}
            </Button>
          )}
          {primaryAction && (
            <Button
              color="primary"
              onClick={() =>
                primaryAction
                  ? skipDispatch
                    ? primaryAction?.action()
                    : dispatch(primaryAction?.action())
                  : null
              }
            >
              {primaryAction?.title}
            </Button>
          )}
        </ModalFooter>
      )}
    </Modal>
  );
};

/**  define proptype for the 'translation' function  */
GenericProcessingModal.contextTypes = {
  t: PropTypes.func,
};

GenericProcessingModal.propTypes = {
  processing: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  processReset: PropTypes.func,
  children: PropTypes.any,
  steps: PropTypes.any,
  title: PropTypes.string,
  primaryAction: PropTypes.any,
  secondaryAction: PropTypes.any,
  skipDispatch: PropTypes.bool,
  deliveryType: PropTypes.oneOf([
    "bowline_relay",
    "contract_call",
    "standard",
    "allowlist_by_tag",
    "unique_mint_vouchers",
  ]),
};

GenericProcessingModal.defaultProps = {};

export default GenericProcessingModal;
